import { useEffect, useState } from "react";

import DetailDrawer from "../components/DetailDrawer.jsx";
import { ApplicantDetailContent } from "../agent/ApplicantDetailDrawer.jsx";
import { useAgentApplicantDetail } from "../agent/useAgentApplicantDetail.js";
import { getWorkerProfileDetailLink } from "../workerDetailedProfile/workerDetailedProfileApi.js";
import ActivationKeyPane from "../workerProfiles/ActivationKeyPane.jsx";

import { DetailSkeleton } from "../components/Skeleton.jsx";
const TITLE_ID = "wx-worker-record-title";

/**
 * The worker record, as one component for the whole backoffice.
 *
 * Owner, 2026-08-08: "หน้าไหนมีรายการแรงงาน ถ้ากดที่แถวให้แสดงแบบนี้ โดยไม่
 * เกี่ยวกับ process คือ user เค้า update ได้ตลอด" - so this is exactly the
 * drawer `/backoffice/agent/workers` opens (`ApplicantDetailContent`), not a
 * per-screen lookalike. A worker clicked from Tracking, SOS, Face Check,
 * Missing Heartbeat, Ready Pool or Worker Profiles reads and edits the same
 * way as on the Agent portal, because it is the same code.
 *
 * Screens pass whichever id they actually hold:
 *  - `applicationId` when the row already is an application, or
 *  - `workerId`, which this resolves to the application behind it through the
 *    real link endpoint (the same one the Worker Profiles screen uses).
 *
 * A worker with no application behind it says so plainly instead of rendering
 * an empty form - that is a real state in this data, not an error.
 */
export default function WorkerRecordDrawer({ open, applicationId = null, workerId = null, onClose, selectableSelector }) {
  const [link, setLink] = useState({ status: "idle", applicationId: null, reason: null });

  useEffect(() => {
    if (!open || applicationId || !workerId) {
      setLink({ status: "idle", applicationId: null, reason: null });
      return undefined;
    }
    let cancelled = false;
    setLink({ status: "loading", applicationId: null, reason: null });
    getWorkerProfileDetailLink(workerId)
      .then((data) => {
        if (cancelled) {
          return;
        }
        if (data?.applicationId) {
          setLink({ status: "ready", applicationId: data.applicationId, reason: null });
          return;
        }
        setLink({
          status: "unavailable",
          applicationId: null,
          reason: data?.unavailableReason ?? "แรงงานคนนี้ยังไม่มีใบสมัครต้นทางผูกอยู่ จึงยังเปิดข้อมูลแบบละเอียดไม่ได้",
        });
      })
      .catch(() => {
        if (!cancelled) {
          setLink({ status: "error", applicationId: null, reason: null });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, applicationId, workerId]);

  const effectiveApplicationId = applicationId ?? link.applicationId;
  const detailState = useAgentApplicantDetail(effectiveApplicationId);

  if (!open) {
    return null;
  }

  return (
    <DetailDrawer open onClose={onClose} labelledBy={TITLE_ID} selectableSelector={selectableSelector} panelClassName="wx-drawer-panel--wide">
      {link.status === "loading" && (
        <DetailSkeleton label="กำลังโหลดข้อมูลแรงงาน..." />
      )}

      {link.status === "unavailable" && (
        <div className="wx-applicant-drawer-state" role="status">
          <h2 id={TITLE_ID} className="wx-state-title">
            ยังไม่มีข้อมูลแรงงานแบบละเอียด
          </h2>
          <p className="wx-state-body">{link.reason}</p>
        </div>
      )}

      {link.status === "error" && (
        <div className="wx-applicant-drawer-state" role="alert">
          <h2 id={TITLE_ID} className="wx-state-title">
            ไม่สามารถเปิดข้อมูลแรงงานได้
          </h2>
          <p className="wx-state-body">กรุณาลองใหม่อีกครั้ง</p>
        </div>
      )}

      {effectiveApplicationId && <ApplicantDetailContent titleId={TITLE_ID} state={detailState} onProfileSaved={detailState.refetch} />}

      {/* The device activation key used to live only on the Worker Profiles
          screen. It belongs to the worker, not to that one screen, so it rides
          with the record everywhere the record is opened - and only when the
          caller actually knows the workerId the endpoint needs. */}
      {workerId && <ActivationKeyPane workerId={workerId} />}
    </DetailDrawer>
  );
}
